import React from "react"

interface SearchTask {
  id: string
  projectId: string
  title: string
  body?: string
  status: number
  priority: number
  projectName?: string
  assigneeName?: string
  createdAt: string
  updatedAt: string
}

interface SearchResultsProps {
  results: SearchTask[]
  isLoading: boolean
  searchQuery: string
  onTaskSelect: (taskId: string) => void
}

const getStatusName = (status: number) => {
  switch (status) {
    case 0:
      return "Todo"
    case 1:
      return "In Progress"
    case 2:
      return "Done"
    default:
      return "Unknown"
  }
}

const getStatusColor = (status: number) => {
  switch (status) {
    case 0:
      return "bg-gray-100 text-gray-800"
    case 1:
      return "bg-blue-100 text-blue-800"
    case 2:
      return "bg-green-100 text-green-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

const getPriorityName = (priority: number) => {
  switch (priority) {
    case 0:
      return "Low"
    case 1:
      return "Normal"
    case 2:
      return "High"
    case 3:
      return "Urgent"
    default:
      return "Normal"
  }
}

const getPriorityColor = (priority: number) => {
  switch (priority) {
    case 0:
      return "bg-green-100 text-green-800"
    case 1:
      return "bg-gray-100 text-gray-800"
    case 2:
      return "bg-orange-100 text-orange-800"
    case 3:
      return "bg-red-100 text-red-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

const highlightText = (text: string, query: string) => {
  if (!query) return text

  const regex = new RegExp(`(${query})`, "gi")
  const parts = text.split(regex)

  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
        {part}
      </mark>
    ) : (
      part
    ),
  )
}

export const SearchResults: React.FC<SearchResultsProps> = ({ results, isLoading, searchQuery, onTaskSelect }) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-card rounded-lg border p-4 animate-pulse">
            <div className="h-5 bg-muted rounded mb-2" />
            <div className="h-4 bg-muted rounded w-3/4 mb-2" />
            <div className="flex gap-2">
              <div className="h-6 bg-muted rounded w-16" />
              <div className="h-6 bg-muted rounded w-16" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!searchQuery || searchQuery.length < 3) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🔍</div>
        <h3 className="text-lg font-medium text-foreground mb-2">Start searching</h3>
        <p className="text-muted-foreground">Enter at least 3 characters to search for tasks</p>
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📭</div>
        <h3 className="text-lg font-medium text-foreground mb-2">No results found</h3>
        <p className="text-muted-foreground">No tasks found for &quot;{searchQuery}&quot;. Try different keywords.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground mb-4">
        Found {results.length} task{results.length !== 1 ? "s" : ""} for &quot;{searchQuery}&quot;
      </div>

      {results.map((task) => (
        <div
          key={task.id}
          onClick={() => onTaskSelect(task.id)}
          className="bg-card rounded-lg border p-4 cursor-pointer transition-colors hover:bg-secondary"
        >
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-foreground">{highlightText(task.title, searchQuery)}</h3>
            <div className="flex gap-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                {getStatusName(task.status)}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                {getPriorityName(task.priority)}
              </span>
            </div>
          </div>

          {task.body && (
            <p className="text-muted-foreground text-sm mb-2 line-clamp-2">{highlightText(task.body, searchQuery)}</p>
          )}

          <div className="flex justify-between items-center">
            <div className="flex gap-4 text-xs text-muted-foreground">
              {task.projectName && <span>📋 {task.projectName}</span>}
              {task.assigneeName && <span>👤 {task.assigneeName}</span>}
            </div>

            <p className="text-xs text-muted-foreground">Updated {new Date(task.updatedAt).toLocaleDateString()}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
